/**
 * This component was made specifically for a Coupon variation for https://rtgjira.atlassian.net/browse/FD-5119
 * It is a copy of the ProductTile component with some modifications to display the coupon variation.
 */
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Stack,
  styled,
  css,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material'
import SellIcon from '@mui/icons-material/SellOutlined'
import { decodeTrademarkEntity } from '@helpers/string-helper'
import { useTest, TEST_ID_CATEGORIES } from '@hooks/useTest'
import { addToDataLayer_nextgen } from '@helpers/google-tag-manager'
import { useProduct } from '@components/product/hooks'
import StrapiLink from '@templates/strapi-cms/content-types/Link'
import { productUrl } from '@helpers/route'
import { navigate } from '@helpers/link'
import SaleFlag from '../../atoms/SaleFlag/SaleFlag'
import ProductPrice from '../../atoms/ProductPrice/ProductTilePrice'
import ProductColor from '../../atoms/ProductColor'

import ProductTileImage from './ProductTileImage'

const StyledCard = styled(Card)`
  // TODO: Remove, this is from global sass files missing in storybooks
  .hide508 {
    clip: rect(1px 1px 1px 1px) !important;
    clip: rect(1px, 1px, 1px, 1px) !important;
    height: 1px !important;
    overflow: hidden !important;
    position: absolute !important;
    white-space: nowrap !important;
    width: 1px !important;
  }

  // Remove android blue outline
  -webkit-tap-highlight-color: transparent;
`

const StyledProductTileButton = styled(Button)`
  font-size: 1rem;
`
const StyledAddToCartButton = styled(Button)`
  width: 85%;
  line-height: 1.875;
`

const StyledTypography = styled(Typography)`
  .free-shipping-tag {
    color: ${({ theme }) => theme.palette.error.main};
  }
`

const ProductTileButtonWrapper = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
`

export default function ProductTileCoupon({
  primary_image,
  alternate_image,
  type,
  productCategory,
  feature,
  title,
  sku,
  price,
  handleClick,
  sale,
  displaySaleFlag,
  room_image,
  isLoading,
  isError,
  route,
  updateVariant,
  inViewRef,
  showVariations = true,
  strikeThroughPrice = 0,
  strikeThrough = false,
  saleFlagLabel,
  isDropShipping,
  isFreeShipping,
  isAvailableForRegion,
  onAddToCart,
  renderAddToCartModal,
  trackingData,
  source,
  product = null,
  isInStock: forceIsInStock,
  showAddToCart: forceShowAddToCart,
  promotion,
  availableColorVariations,
}) {
  const {
    routeToSimilarItems,
    isInStock,
    showAddToCart,
    isThankyouDiscount,
    isMattressOnly,
    promoMessageHTML,
  } = useProduct({
    product,
  })
  const theme = useTheme()
  const [isTooltipHover, setIsTooltipHover] = useState(false)
  const { generateTestId } = useTest()
  const [variantLimit, setVariantLimit] = useState(5)

  const onUpdateVariant = selectedSku => updateVariant(selectedSku)

  const priceWithPromoFee = useMemo(() => (price ? price * 1.02 : 0), [price])
  const [productDollars, productCents] = priceWithPromoFee.toFixed(2).split('.')

  const displayButton = () => (
    <StyledProductTileButton
      onClick={() => navigate(productUrl(product?.route))}
      color="primary"
      fullWidth
      variant="contained"
      aria-label={`View Details of ${title}`}
    >
      View Details
    </StyledProductTileButton>
  )

  //  Coupon Attributes
  const couponValidity = product.couponDisplayFields.validThruMessage

  useEffect(() => {
    const handleResize = () =>
      setVariantLimit(() => {
        let value = 5

        if (window.innerWidth < 748) value = 5
        else if (window.innerWidth < 940) value = 3
        else if (window.innerWidth < 1024) value = 4
        else if (window.innerWidth < 1080) value = 2
        else if (window.innerWidth < 1280) value = 3
        else if (window.innerWidth < 1160) value = 4

        return value
      })
    handleResize()

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [setVariantLimit])

  if (isLoading || !sku)
    return (
      <Stack direction="column" ref={inViewRef}>
        <Skeleton width="100%" sx={{ aspectRatio: '3/2' }} />
        <Box sx={{ pt: 0.5 }}>
          <Skeleton />
          <Skeleton width="60%" />
        </Box>
      </Stack>
    )
  return (
    <StrapiLink
      className="product-image-link"
      data={{
        InternalUrl: route,
        testId: generateTestId(TEST_ID_CATEGORIES.PRODUCT_TITLE, sku),
        ...(!isTooltipHover && { Title: title }),
      }}
      trackingData={trackingData}
      disableUnderline
    >
      <StyledCard
        square
        variant="outlined"
        sx={{
          overflow: 'visible',
          width: '100%',
          position: 'relative',
          height: '100%',
          borderRadius: '8px',
          border: '3px dashed #07263b',
          paddingRight: '0',
        }}
        ref={inViewRef}
      >
        {isError && <div>Error {JSON.stringify(isError)}</div>}
        {displaySaleFlag && <SaleFlag label={saleFlagLabel} />}
        {renderAddToCartModal()}
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Product Images */}
          <ProductTileImage
            onClick={handleClick}
            primaryImage={type === 'room' && room_image ? room_image : primary_image}
            alternateImage={alternate_image}
            title={title}
            showAddToCartIcon={isAvailableForRegion && productCategory !== 'mattress bedding'}
            onAddToCartClick={onAddToCart}
          />
          <CardContent
            sx={{
              flex: 1,
              justifyContent: 'space-between',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: '16px',
            }}
          >
            {/* Main */}
            {availableColorVariations?.length > 0 && showVariations && (
              <Box
                display="flex"
                mb="20px"
                alignItems="center"
                sx={{
                  gap: '4px',
                  ':empty': {
                    display: 'none',
                  },
                }}
              >
                {availableColorVariations?.map(
                  (variant, key) =>
                    key < variantLimit &&
                    variant.variation_value !== '' && (
                      <ProductColor
                        disableEventPropagation
                        key={`color-variant-${variant.sku}`}
                        sx={{
                          marginRight: '5px',
                        }}
                        width="44px"
                        height="44px"
                        isSelected={sku === variant.sku}
                        color={variant}
                        onClick={e => onUpdateVariant(e)}
                      />
                    ),
                )}
                {availableColorVariations?.length > variantLimit && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="38px"
                    height="38px"
                    minWidth="38px"
                    borderRadius="19px"
                    border="1px solid"
                    borderColor={theme.palette.secondary.main}
                    marginLeft="3px"
                  >
                    <Typography fontSize="16px" fontWeight="600" color={theme.palette.secondary.lighter}>
                      +{availableColorVariations?.length - variantLimit}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <Box
              sx={{
                flex: 1,
                marginBottom: '8px',
              }}
            >
              {/* Coupon Title */}
              <div style={{ display: 'flex' }}>
                <SellIcon style={{ color: theme.palette.error.main }} />
                <Typography
                  fontSize="23px"
                  variant="body1"
                  component="h3"
                  lineHeight="24px"
                  fontWeight="500"
                  marginBottom="8px"
                  marginLeft="8px"
                  textTransform="none"
                  color={theme.palette.error.main}
                  title={title}
                  data-testid={generateTestId('product-tile', 'title')}
                  sx={{
                    textTransform: 'capitalize',
                    ':empty': {
                      display: 'none',
                    },
                  }}
                >
                  {product?.couponDisplayFields?.title.toLowerCase() || 'Missing Coupon Title'}
                </Typography>
              </div>

              {/* Product Title */}
              {title && (
                <Typography
                  fontSize="16px"
                  variant="body1"
                  component="h2"
                  fontWeight="600"
                  marginBottom="8px"
                  textTransform="none"
                  color={theme.palette.primary.dark}
                  title={title}
                  data-testid={generateTestId('product-tile', 'title')}
                  sx={{
                    ':empty': {
                      display: 'none',
                    },
                  }}
                >
                  {decodeTrademarkEntity(title)}
                </Typography>
              )}
              {/* Product Price */}
              {isAvailableForRegion && (
                <ProductPrice
                  dataTestId={generateTestId('product-tile', 'price')}
                  fontSize="23px"
                  price={price}
                  sale={sale}
                  strikeThrough={strikeThrough}
                  strikeThroughPrice={strikeThroughPrice}
                  highlightPrice={saleFlagLabel?.toLowerCase() === 'last chance' || isThankyouDiscount}
                />
              )}
            </Box>
            {/* Additional Info */}
            <div>
              <Typography
                fontSize="14px"
                variant="body1"
                component="h3"
                fontWeight="400"
                textTransform="none"
                color="#07263b"
                title={title}
                data-testid={generateTestId('product-tile', 'title')}
              >
                {product?.couponDisplayFields?.additionalDisclaimer}
              </Typography>
              <Typography
                fontSize="14px"
                variant="body1"
                component="h3"
                lineHeight="24px"
                fontWeight="400"
                textTransform="none"
                color="#07263b"
                title={title}
                data-testid={generateTestId('product-tile', 'title')}
              >
                {product?.couponDisplayFields?.caption}
              </Typography>
              <Typography
                fontSize="14px"
                variant="body1"
                component="h3"
                lineHeight="24px"
                fontWeight="400"
                textTransform="none"
                color="#07263b"
                title={title}
                data-testid={generateTestId('product-tile', 'title')}
                paddingBottom={2}
              >
                {couponValidity}
              </Typography>
              {/*  TODO we never want to show Add to Cart? */}
              <ProductTileButtonWrapper>{displayButton()}</ProductTileButtonWrapper>
            </div>
          </CardContent>
        </Box>
      </StyledCard>
    </StrapiLink>
  )
}

ProductTileCoupon.propTypes = {
  primary_image: PropTypes.string,
  alternate_image: PropTypes.string,
  room_image: PropTypes.string,
  route: PropTypes.string,
  type: PropTypes.string,
  productCategory: PropTypes.string,
  feature: PropTypes.string,
  title: PropTypes.string,
  sku: PropTypes.string,
  price: PropTypes.number,
  strikeThroughPrice: PropTypes.number,
  strikeThrough: PropTypes.bool,
  sale: PropTypes.bool,
  displaySaleFlag: PropTypes.bool,
  handleClick: PropTypes.func,
  updateVariant: PropTypes.func,
  inViewRef: PropTypes.func.isRequired /** Required for InView logic of useProductQuery */,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  showVariations: PropTypes.bool,
  saleFlagLabel: PropTypes.string,
  isDropShipping: PropTypes.bool,
  isFreeShipping: PropTypes.bool,
  isAvailableForRegion: PropTypes.bool,
  onAddToCart: PropTypes.func,
  renderAddToCartModal: PropTypes.func,
  trackingData: PropTypes.object,
  source: PropTypes.string,
  product: PropTypes.object,
  isInStock: PropTypes.bool,
  showAddToCart: PropTypes.bool,
  rating: PropTypes.shape({
    reviews: PropTypes.number,
    averageRating: PropTypes.number,
    ratings: PropTypes.number,
  }),
  promotion: PropTypes.shape({
    additional_qualifier_sku: PropTypes.array,
  }),
  availableColorVariations: PropTypes.array,
}
